<template>
    <div class="widget-component">
        <v-container fluid id="widget-viewings-frame" class="widget-viewings">
            <v-container>
                <main-menu
                    v-if="normalizedOptions.menu.show"
                    :root-url="rootUrl"
                    :loginDialog="loginDialog"
                    :accessToken="accessToken"
                    :show-login-if-logged-out="true"
                    @loggedIn="handleLoginEmit"
                    :show-elements="normalizedOptions.menu.show"
                    @showLoginDialog="handleLoginDialogEmit"
                >
                </main-menu>
                <v-row v-if="normalizedOptions.title.show">
                    <v-col cols="12">
                        <h1 class="mb-4">
                            {{ normalizedOptions.title.text }}
                        </h1>
                    </v-col>
                </v-row>
                <v-row v-if="loading" v-for="index in 3" :key="index">
                    <v-col cols="10" class="px-0">
                        <v-row>
                            <v-col cols="3" class="px-0">
                                <v-skeleton-loader type="image"></v-skeleton-loader>
                            </v-col>
                            <v-col cols="7">
                                <v-row>
                                    <v-col cols="12" class="pb-2">
                                        <v-skeleton-loader class="pb-0 pt-0 pl-0"
                                                           type="card-heading"></v-skeleton-loader>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12">
                                        <v-skeleton-loader class="pb-0 pt-0 pl-0" type="paragraph"></v-skeleton-loader>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12">
                                        <v-skeleton-loader class="pb-0 pt-0 pl-0" type="actions"></v-skeleton-loader>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row class="mb-6">
                    <v-col v-for="application in viewings" :key="application.id" cols="12" md="6" lg="4">
                        <v-card>
                            <v-img
                                v-if="has(application,'listing.mainImage.thumbnail')"
                                :src="application.listing.mainImage.thumbnail"
                                gradient="to top, rgba(0,0,0,.70), rgba(0,0,0,.20)" >

                                <v-row align="end" style="height: 100%">
                                    <v-col cols="12" align-self="end">
                                        <v-card-title class="white--text pb-0">
                                            <v-row class="mx-0" style="width: 100%">
                                                <a class="text-decoration-none white--text"
                                                   v-if="has(application,'listing.name')"
                                                   :href="generateURL(application.listing)">
                                                    {{ application.listing.name }}
                                                </a>
                                            </v-row>

                                            <v-row class="mx-0 mt-2" style="width: 100%">
                                                <small class="white--text" v-if="has(application,'listing.displayAddress')">
                                                    {{ application.listing.displayAddress }}
                                                </small>
                                            </v-row>
                                        </v-card-title>
                                    </v-col>
                                </v-row>
                            </v-img>

                            <v-card-text class="pb-0">
                                <div class="font-weight-bold ml-8 mb-2">
                                    Appointments
                                </div>

                                <v-timeline
                                    align-top
                                    dense>
                                    <v-timeline-item
                                        v-for="(viewing, i) in sortedDates(application.upcomingAppointments)"
                                        :key="i"
                                        :color="viewing.status == 'cancelled' ? 'secondary' : 'primary'"
                                        :icon="viewing.status == 'cancelled' ? 'mdi-cancel' : (dateInPast(viewing.dateTime) ? 'mdi-history' : 'mdi-close')"
                                        @click.native="triggerCancelViewing(viewing)">

                                        <div class="mb-2" v-if="has(viewing, 'title') && !application.listing">
                                            {{ viewing.title }}
                                        </div>

                                        <div>
                                            <div class="font-weight-normal">
                                                <strong>{{ formatDate(new Date(viewing.dateTime)) }}</strong>
                                            </div>

                                            <small v-if="dateInPast(viewing.dateTime) && viewing.status != 'cancelled'">
                                                Attended
                                            </small>

                                            <small v-else-if="viewing.status != 'cancelled'">
                                                Approx. {{ viewing.durationMin }} minutes
                                            </small>

                                            <small v-else>
                                                Cancelled
                                            </small>
                                        </div>
                                    </v-timeline-item>
                                </v-timeline>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-row v-if="viewings.length == 0">
                        <v-col>
                            <v-alert type="info" class="mb-10">
                                You currently do not have any viewings booked.
                            </v-alert>
                        </v-col>
                    </v-row>

                    <v-dialog
                        :value="!cancelId ? false : true"
                        persistent
                        max-width="320"
                    >
                        <v-card>
                            <v-card-title class="text-h5">
                                Cancel Appointment?
                            </v-card-title>
                            <v-card-text>
                                Once you cancel the appointment,
                                the slot will be available for others to book.
                                You will have the ability to make another booking
                                for any available slots.
                            </v-card-text>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn
                                    text
                                    color="secondary darken-1"
                                    @click="cancelId = null"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    v-if="cancelId"
                                    color="primary darken-1"
                                    @click="cancelViewing(cancelId)"
                                >
                                    Confirm
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-row>
            </v-container>
        </v-container>
    </div>
</template>

<script>
import {defineComponent, ref, onMounted, computed, nextTick} from '@vue/composition-api';
import {has, merge, orderBy} from "lodash";
import MainMenu from "./partials/MainMenu";
import ProfileService from "../services/ProfileService";


export default defineComponent({
    name: 'Viewings',
    components: {MainMenu},
    props: {
        rootUrl: {
            type: String,
            required: true
        },
        accessToken: {
            type: String,
        },
        settings: {
            type: Object
        },
        listingsUrl: {
            type: String,
            default: null
        },
    },
    setup(props) {
        const loggedIn = ref(false);
        const loggedInProfile = ref(false);
        const cancelId = ref(null);
        const loading = ref(false);
        const applications = ref([]);
        const token = ref(null);
        const loginDialog = ref(false);
        const activeTab = ref('viewings');

        const defaultSettings = ref({
            enabled: true,
            title: {
                show: true,
                text: 'Appointments',
            },
            menu: {
                show: false
            },
            events: {
                redirectIfNotLoggedIn: '/login',
            }
        });

        const normalizedOptions = computed(() => {
            return merge(defaultSettings.value, props.settings);
        });

        const viewings = computed(() => {
            return applications.value.filter(function (obj) {
                return obj.upcomingAppointments.length != 0 && !obj.isArchived;
            });
        });

        function getApplications() {
            loading.value = true;
            const withListings = false;
            ProfileService.getApplications(withListings).then((response) => {
                if (has(response, 'data.data')) {
                    applications.value = response.data.data;
                }
            })
                .catch(error => {
                    console.error(error);
                    if (has(error, 'response.data')) {
                        //errors.value = error.response.data;
                    }
                })
                .finally(() => {
                    loading.value = false;
                });
        }

        function triggerCancelViewing(viewing) {
            if (!dateInPast(viewing.dateTime) && viewing.status !== 'cancelled') {
                cancelId.value = viewing.id;
            }
        }

        function cancelViewing(id) {
            loading.value = true;
            ProfileService.cancelViewing(id)
                .then(function (response) {
                })
                .catch(function (error) {
                    console.error(error);
                })
                .then(function () {
                    cancelId.value = null;
                    getApplications();
                    loading.value = false;
                });
        }

        function dateInPast(date) {
            const today = new Date();
            const newDate = new Date(date);

            if (newDate <= today.setHours(0, 0, 0, 0)) {
                return true;
            }

            return false;
        }

        function generateURL(listing) {
            const listingSlug = has(listing, 'widgetSiteSettings.customSlug') && listing.widgetSiteSettings.customSlug ?
                listing.widgetSiteSettings.customSlug : listing.slug;

            return props.listingsUrl ? props.listingsUrl.replace("{slug}", listingSlug) :
                props.rootUrl + '/listing/' + listingSlug;
        }

        function formatDate(originalDate) {
            const options = {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
                timeZone: 'UTC'
            };

            const formattedDate = originalDate.toLocaleDateString('en-GB', options);

            return `${formattedDate}`;
        }

        function sortedDates(dates) {
            return orderBy(dates, 'dateTime')
        }

        function handleLoginEmit(data) {
            if (data.loggedInProfile) {
                loggedInProfile.value = data.loggedInProfile;
            }
            if (data.accessToken) {
                token.value = data.accessToken;
            }
            getApplications();
        }

        function handleLoginDialogEmit(val) {
            loginDialog.value = val;
        }

        function redirectToLogin() {
            if (has(normalizedOptions.value, 'events.redirectIfNotLoggedIn')) {
                window.location.href = normalizedOptions.value.events.redirectIfNotLoggedIn;
            }
        }


        function init() {
            loggedIn.value = false;
            ProfileService.getProfile()
                .then(response => {
                    loggedIn.value = true;
                    loggedInProfile.value = response;
                    getApplications();
                })
                .catch(error => {
                    loggedIn.value = false;
                    redirectToLogin();
                });
        }

        onMounted(() => {
            init();
        });


        return {
            // Variables
            cancelId,
            token,
            loading,
            applications,
            viewings,
            loginDialog,
            loggedInProfile,
            activeTab,
            normalizedOptions,

            // Functions
            generateURL,
            formatDate,
            sortedDates,
            cancelViewing,
            dateInPast,
            handleLoginEmit,
            handleLoginDialogEmit,
            triggerCancelViewing,
            has

        }

    }
});
</script>

<style lang="scss">
    .widget-viewings{
        .v-timeline{
            .v-timeline-item{
                &:hover{
                    cursor: pointer;

                    .v-timeline-item__inner-dot{
                        border-color: var(--v-secondary-base) !important;
                        background: var(--v-secondary-base) !important;
                    }
                }
            }
        }
    }
</style>
